import React, { useEffect } from "react";
import {
  Badge,
  Box,
  Button,
  Drawer,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { CONTRACTOR_NAV_ITEMS, OEM_NAV_ITEMS } from "@/constants";
import { NavLink } from "react-router-dom";
import { useAuthStore } from "@/store/authStore.ts";
import { StyledIconButton } from "@/components/styled";
import SvgBurgerMenu from "@/assets/icons/SVGBurgerMenu.tsx";
import AdminDropdown from "@/components/AdminDropdown";
import { useNewsStore } from "@/store/newsStore.ts";

export const NavigationWrapperBox = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "10px",
  overflowX: "auto",
  overflowY: "visible",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    alignItems: "center",
  },
}));

const StyledNavigationBadge = styled(Badge)(() => ({
  "& .MuiBadge-badge": {
    right: 18,
    top: 10,
    fontSize: "8px",
    height: "14px",
    minWidth: "25px",
    fontWeight: 600,
    padding: "0 6px",
  },
}));

const StyledNavigationButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ theme, isActive }) => ({
  ...(isActive && {
    backgroundColor: theme.palette.navigationActive,
  }),
}));

const Navigation = () => {
  const role = useAuthStore((state) => state.role);
  const trainingCompleted = useAuthStore((state) => state.trainingCompleted);
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();

  const hasNewArticles = useNewsStore((state) => state.hasNewArticles);
  const getNews = useNewsStore((state) => state.getNews);

  useEffect(() => {
    getNews("");
  }, [getNews]);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const downMd = useMediaQuery(theme.breakpoints.down("md"));

  const NavigationList = (
    <NavigationWrapperBox>
      {role === "administrator" && (
        <>
          <AdminDropdown />
          <NavLink to="/map">
            {({ isActive }) => (
              <StyledNavigationButton
                variant={"navigation"}
                isActive={isActive}
              >
                Map
              </StyledNavigationButton>
            )}
          </NavLink>
          <NavLink to="/user-management">
            {({ isActive }) => (
              <StyledNavigationButton
                variant={"navigation"}
                isActive={isActive}
              >
                Accounts
              </StyledNavigationButton>
            )}
          </NavLink>
          <NavLink to="/files-management">
            {({ isActive }) => (
              <StyledNavigationButton
                variant={"navigation"}
                isActive={isActive}
              >
                Files
              </StyledNavigationButton>
            )}
          </NavLink>
          <NavLink to="/projects">
            {({ isActive }) => (
              <StyledNavigationButton
                variant={"navigation"}
                isActive={isActive}
              >
                Projects
              </StyledNavigationButton>
            )}
          </NavLink>
          <NavLink to="/updates">
            {({ isActive }) => (
              <StyledNavigationBadge
                badgeContent="New"
                color="error"
                invisible={!hasNewArticles}
              >
                <StyledNavigationButton
                  variant={"navigation"}
                  isActive={isActive}
                >
                  Updates
                </StyledNavigationButton>
              </StyledNavigationBadge>
            )}
          </NavLink>
        </>
      )}
      {role === "contractor" &&
        trainingCompleted &&
        CONTRACTOR_NAV_ITEMS.map((el, i) => (
          <NavLink key={i} to={el.navTo}>
            {({ isActive }) => {
              const isUpdatesPage = el.navTo === "/updates";
              return isUpdatesPage ? (
                <StyledNavigationBadge
                  badgeContent="New"
                  color="error"
                  invisible={!hasNewArticles}
                >
                  <StyledNavigationButton
                    variant={"navigation"}
                    isActive={isActive}
                  >
                    {el?.label}
                  </StyledNavigationButton>
                </StyledNavigationBadge>
              ) : (
                <StyledNavigationButton
                  variant={"navigation"}
                  isActive={isActive}
                >
                  {el?.label}
                </StyledNavigationButton>
              );
            }}
          </NavLink>
        ))}
      {role === "oem" &&
        OEM_NAV_ITEMS.map((el, i) => (
          <NavLink key={i} to={el.navTo}>
            {({ isActive }) => {
              const isUpdatesPage = el.navTo === "/updates";
              return isUpdatesPage ? (
                <StyledNavigationBadge
                  badgeContent="New"
                  color="error"
                  invisible={!hasNewArticles}
                >
                  <StyledNavigationButton
                    variant={"navigation"}
                    isActive={isActive}
                  >
                    {el?.label}
                  </StyledNavigationButton>
                </StyledNavigationBadge>
              ) : (
                <StyledNavigationButton
                  variant={"navigation"}
                  isActive={isActive}
                >
                  {el?.label}
                </StyledNavigationButton>
              );
            }}
          </NavLink>
        ))}
    </NavigationWrapperBox>
  );

  if (downMd) {
    return (
      <>
        <StyledIconButton onClick={toggleDrawer(true)}>
          <SvgBurgerMenu />
        </StyledIconButton>
        <Drawer
          PaperProps={{ sx: { padding: "16px", backgroundColor: "#003257" } }}
          open={open}
          onClose={toggleDrawer(false)}
        >
          {NavigationList}
        </Drawer>
      </>
    );
  } else {
    return NavigationList;
  }
};

export default Navigation;
